import React, { useMemo, useState } from "react";
import {
  BookOpen, FlaskConical, Cpu, Bot, MonitorPlay, Dumbbell, Timer, Waves,
  Activity, Theater, Brush, Music, UtensilsCrossed, Bus, Trees,
  ShieldCheck, HeartPulse, Wifi, Sun, Maximize2, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { infrastructure, infraCategories, infraHighlights } from "../mock/mock";
import { SectionHeading } from "./SectionHeading";

const icons = {
  BookOpen, FlaskConical, Cpu, Bot, MonitorPlay, Dumbbell, Timer, Waves,
  Activity, Theater, Brush, Music, UtensilsCrossed, Bus, Trees,
  ShieldCheck, HeartPulse, Wifi, Sun,
};

export default function Infrastructure() {
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(
    () => (cat === "All" ? infrastructure : infrastructure.filter((i) => i.category === cat)),
    [cat]
  );

  const move = (dir) => {
    const n = (lightbox + dir + items.length) % items.length;
    setLightbox(n);
  };

  return (
    <section id="infrastructure" className="relative py-24 lg:py-32" style={{ background: "var(--sand)" }}>
      <div className="mx-auto max-w-[1280px] px-5 lg:px-6">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Infrastructure"
            title="A 160-acre campus engineered for childhood"
            sub="Every facility exists for one reason — so a child can try something today that they could not do yesterday. Labs, arenas, studios and libraries, all within a safe, walkable, green campus."
            className="lg:max-w-[62%]"
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:w-[34%]" data-reveal="">
            {infraHighlights.map((h) => {
              const Icon = icons[h.icon];
              return (
                <div
                  key={h.label}
                  className="flex items-start gap-3 rounded-xl bg-white/70 p-4"
                  style={{ border: "1px solid rgba(11,59,54,.08)" }}
                >
                  <Icon size={17} strokeWidth={1.7} style={{ color: "var(--gold)", marginTop: 2 }} />
                  <span className="text-[0.8rem] font-medium leading-snug" style={{ color: "var(--teal-800)" }}>
                    {h.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-14 flex flex-wrap gap-2.5" data-reveal="">
          {infraCategories.map((c) => {
            const active = c === cat;
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                className="rounded-full px-5 py-2.5 text-[0.82rem] font-semibold"
                style={{
                  background: active ? "var(--teal-800)" : "rgba(255,255,255,.75)",
                  color: active ? "#FBF7F0" : "var(--teal-800)",
                  border: `1px solid ${active ? "var(--teal-800)" : "rgba(11,59,54,.14)"}`,
                  transition: "background .3s ease, color .3s ease, border-color .3s ease",
                }}
              >
                {c}
                {c !== "All" && (
                  <span className="ml-2 opacity-55">
                    {infrastructure.filter((i) => i.category === c).length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Bento grid */}
        <div
          className="mt-9 grid auto-rows-[300px] gap-5 sm:grid-cols-2 lg:grid-cols-3"
          style={{ gridAutoFlow: "dense" }}
        >
          {items.map((it, i) => {
            const Icon = icons[it.icon] || BookOpen;
            const wide = it.span === "wide" && cat === "All";
            return (
              <button
                key={it.title}
                data-reveal=""
                onClick={() => setLightbox(i)}
                style={{ transitionDelay: `${(i % 6) * 70}ms` }}
                className={`group relative overflow-hidden rounded-2xl text-left ${
                  wide ? "lg:col-span-2" : ""
                }`}
              >
                <img
                  src={it.image}
                  alt={it.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-110"
                  style={{ transition: "transform 1s cubic-bezier(.22,1,.36,1)" }}
                />
                <span
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(5,32,29,.92) 0%, rgba(5,32,29,.55) 42%, rgba(5,32,29,.10) 100%)",
                  }}
                />

                <span
                  className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full opacity-0 group-hover:opacity-100"
                  style={{ background: "rgba(251,247,240,.9)", color: "var(--teal-800)", transition: "opacity .3s ease" }}
                >
                  <Maximize2 size={15} />
                </span>

                <span className="absolute inset-x-0 bottom-0 p-7">
                  <span className="flex items-center gap-2.5">
                    <Icon size={16} strokeWidth={1.8} style={{ color: "var(--gold-400)" }} />
                    <span className="eyebrow" style={{ color: "var(--gold-200)", fontSize: "0.66rem" }}>
                      {it.stat}
                    </span>
                  </span>
                  <span
                    className="font-display mt-3 block text-[1.45rem] leading-tight"
                    style={{ color: "#FBF7F0" }}
                  >
                    {it.title}
                  </span>
                  <span
                    className="mt-2 block max-h-0 overflow-hidden text-[0.85rem] leading-relaxed opacity-0 group-hover:max-h-32 group-hover:opacity-100"
                    style={{ color: "rgba(251,247,240,.82)", transition: "max-height .5s ease, opacity .4s ease" }}
                  >
                    {it.text}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && items[lightbox] && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: "rgba(5,32,29,.94)", backdropFilter: "blur(6px)" }}
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full"
            style={{ border: "1px solid rgba(251,247,240,.3)", color: "#FBF7F0" }}
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <button
            className="absolute left-4 grid h-12 w-12 place-items-center rounded-full sm:left-8"
            style={{ border: "1px solid rgba(251,247,240,.3)", color: "#FBF7F0" }}
            onClick={(e) => { e.stopPropagation(); move(-1); }}
            aria-label="Previous"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            className="absolute right-4 grid h-12 w-12 place-items-center rounded-full sm:right-8"
            style={{ border: "1px solid rgba(251,247,240,.3)", color: "#FBF7F0" }}
            onClick={(e) => { e.stopPropagation(); move(1); }}
            aria-label="Next"
          >
            <ChevronRight size={20} />
          </button>

          <div
            className="fade-up w-full max-w-[900px] overflow-hidden rounded-2xl"
            style={{ background: "var(--cream)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={items[lightbox].image}
              alt={items[lightbox].title}
              className="h-[46vh] w-full object-cover sm:h-[54vh]"
            />
            <div className="p-7 sm:p-9">
              <span className="eyebrow" style={{ color: "var(--gold)" }}>
                {items[lightbox].category} · {items[lightbox].stat}
              </span>
              <h3 className="font-display mt-3 text-[1.8rem]" style={{ color: "var(--teal-900)" }}>
                {items[lightbox].title}
              </h3>
              <p className="mt-3 text-[0.95rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                {items[lightbox].text}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
